import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/ui/table/data-table";
import { useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { displayValue, displayDate, formatToPKR } from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import { ActiveMembershipPlan } from "@/types/member";
import { Separator } from "@/components/ui/separator";
import { TableResponse } from "@/types";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { mockActiveMembershipPlanResponse } from "@/mock/member-membership";
import { MembershipStatus } from "@/types/enums";
import { useGetMemberMembershipQuery, useUpdateMembershipStatusMutation } from "@/services/memberApi";
import { ErrorType } from "@/types";
import { toast } from "@/components/ui/use-toast";
import { status } from "@/lib/constants/global";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const initialValue = {
  limit: 5,
  offset: 0,
  sort_order: "desc",
  sort_key: "membership_plan_id",
};

const MemberActiveMemberships = () => {
  const { memberId } = useParams();
  const [searchCriteria, setSearchCriteria] = useState(initialValue);
  const [query, setQuery] = useState("");
  const [updateMembershipStatus] = useUpdateMembershipStatusMutation()
  useEffect(() => {
    setSearchCriteria((prev) => {
      const newCriteria = { ...prev };
      newCriteria.offset = 0;
      newCriteria.sort_key = "membership_plan_id";
      newCriteria.sort_order = "desc";
      return newCriteria;
    });
  }, [setSearchCriteria]);

  useEffect(() => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(searchCriteria)) {
      console.log({ key, value });
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    }
    const newQuery = params.toString();
    console.log({ newQuery });
    setQuery(newQuery);
  }, [searchCriteria]);

  const {
    data: membershipData,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetMemberMembershipQuery(
    { member_id: Number(memberId), query: query },
    {
      skip: query == "",
    }
  );

  const tableData = useMemo(() => {
    return Array.isArray(membershipData?.data) ? membershipData?.data : [];
  }, [membershipData]);

  const toggleSortOrder = (key: string) => {
    setSearchCriteria((prev) => ({
      ...prev,
      sort_key: key,
      sort_order:
        prev.sort_key === key && prev.sort_order === "desc" ? "asc" : "desc",
    }));
  };

  const handleStatusChange = async (payload: { status: MembershipStatus, membership_plan_id: number, client_id: number, contract_id: number }) => {
    try {
      const resp = await updateMembershipStatus(payload).unwrap();
      if (resp.status_code == 200) {
        toast({ variant: "success", title: "Membership status updated successfully" })
        return;
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  }


const columns: ColumnDef<ActiveMembershipPlan>[] = [
  {
    accessorKey: "membership_plan_name",
    meta: "membership_plan_name",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Membership Name"
        sortKey="key"
        searchCriteria={searchCriteria} // Pass current search criteria
        toggleSortOrder={() => toggleSortOrder("membership_plan_name")}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex capitalize items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
          {displayValue(row?.original.membership_plan_name)}
        </div>
      );
    },
  },
  {
    accessorKey: "group",
    meta: "group",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Group"
        sortKey="key"
        searchCriteria={searchCriteria} // Pass current search criteria
        toggleSortOrder={() => toggleSortOrder("group")}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex capitalize items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
          {displayValue(row?.original.membership_plan_group)}
        </div>
      );
    },
  },
  {
    accessorKey: "duration",
    meta: "duration",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Duration"
        sortKey="key"
        searchCriteria={searchCriteria} // Pass current search criteria
        toggleSortOrder={() => toggleSortOrder("membership_plan_duration")}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
          {displayValue(row?.original.membership_plan_duration)}
        </div>
      );
    },
  },
  {
    accessorKey: "total_amount",
    meta: "net_amount",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Net Amount"
        sortKey="key"
        searchCriteria={searchCriteria} // Pass current search criteria
        toggleSortOrder={() => toggleSortOrder("membership_plan_price")}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
          {displayValue(formatToPKR(row.original.membership_plan_price))}
        </div>
      );
    },
  },
  {
    accessorKey: "membership_status",
    meta: "membership_status",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Membership Status"
        sortKey="key"
        searchCriteria={searchCriteria} // Pass current search criteria
        toggleSortOrder={() => toggleSortOrder("membership_plan_status")}
      />
    ),
    cell: ({ row }) => {
      const value = row?.original?.membership_plan_status
      const statusLabel = status.find((r) => r.value === value);
      const client_id = Number(memberId);
      const membership_plan_id = row.original.membership_plan_id
      const contract_id = row.original.contract_id
      return (
        <Select
          defaultValue={value}
          onValueChange={(e) =>
            handleStatusChange({ contract_id, membership_plan_id, client_id, status: e as MembershipStatus })
          }
        >
          <SelectTrigger className="h-8 w-40">
            <SelectValue placeholder="Status" className="text-gray-400">
              <span className="flex gap-2 items-center">
                <span
                  className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                ></span>
                <span>{statusLabel?.label}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {status.map((item) => (
              <SelectItem key={item.value + ""} value={item.value + ""}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    },
  },
];
useEffect(() => {
  if (isError) {
    const typedError = error as ErrorType;
    toast({
      variant: "destructive",
      title: "Error",
      description: typedError.data?.detail || typedError.data?.message,
    });
  }
}, [isError]);

const totalRecords = membershipData?.filtered_counts || 0;

const {
  handleLimitChange,
  handleNextPage,
  handlePrevPage,
  handleFirstPage,
  handleLastPage,
  isLastPage,
} = usePagination({
  totalRecords,
  searchCriteria,
  setSearchCriteria,
});

return (
  <>
    <h1 className="font-bold text-xl mb-2 ">Memership Plans</h1>
    <Separator />
    {/* Data Table */}
    <div className="mt-4 bg-white">
      <DataTable columns={columns} data={tableData} isLoading={isLoading} />{" "}
      {/* //isLoading */}
    </div>

    {/* Pagination */}
    {tableData.length > 0 && (
      <Pagination
        limit={searchCriteria.limit}
        offset={searchCriteria.offset}
        totalItems={totalRecords}
        onLimitChange={handleLimitChange}
        onNextPage={handleNextPage}
        onPrevPage={handlePrevPage}
        onFirstPage={handleFirstPage}
        onLastPage={handleLastPage}
        isLastPage={isLastPage}
      />
    )}
  </>
);
};

export default MemberActiveMemberships;
